/* eslint-disable */
// @ts-nocheck

import { useParams, useNavigate } from "react-router-dom";
import { useState, useRef, useEffect, useContext } from "react";
import { Button } from "antd";
import { PiBriefcaseBold } from "react-icons/pi";
import { PageTitle } from "../../components/PageTitle";
import { GenericChat } from "../../components/shared/chat";
import { createAdapter } from "../../components/shared/chat";
import { JobsListComponent, StoredJobsButtonComponent, AiJobsSummaryCard } from "../../components/shared/chat/features";
import { MainContainer } from "../../components/shared/chat";
import { useSmartSessionLoader, useSessionMessages, useChatSessions } from "../../components/shared/chat";
import { useAiJobs } from "../../hooks/useAiJobs";
import { useChatAuth } from "../../components/shared/chat";
import { AuthContext } from "../../context/AuthContext";
import { AiPreview } from "../aiPreview/AiPreview";
import aiPreviewsImg from "../../assets/aipreviews.png";

export const AiJobs = () => {
    const { canAccess } = useContext(AuthContext);
    const hasActiveSubscription = canAccess('ai-jobs');

    if (!hasActiveSubscription) {
        return (
            <div>
                <MainContainer>
                    <div style={{
                        position: 'relative',
                        height: 'calc(100vh - 100px)',
                        borderRadius: 12,
                        overflow: 'hidden',
                    }}>
                        <img src={aiPreviewsImg} alt="AI Jobs" style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                        }} />
                        <div style={{ position: 'relative', zIndex: 1, height: '100%' }}>
                            <AiPreview />
                        </div>
                    </div>
                </MainContainer>
            </div>
        );
    }

    return <AiJobsContent />;
};

const AiJobsContent = () => {
    const { sessionId } = useParams<{ sessionId: string }>();
    const navigate = useNavigate();
    const adapter = createAdapter('aiJobs');
    const [activeTab, setActiveTab] = useState<string | null>(null);
    const [isRetryingJobs, setIsRetryingJobs] = useState(false);
    const retryCountRef = useRef(0);
    const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);

    // Smart session loading - resolves to sessionId or loading state
    const { resolvedSessionId, isLoading } = useSmartSessionLoader('job_search', sessionId || null);

    // Get candidate ID for React Query
    const { candidateId } = useChatAuth();

    // Always prioritize URL sessionId over resolved sessionId
    const effectiveSessionId = sessionId || resolvedSessionId;

    const { data: aiJobs, refetch: refetchAiJobs, isLoading: isLoadingJobs } = useAiJobs(candidateId, effectiveSessionId, !!candidateId);

    // Get session messages to pass to JobsList for summary card
    const { messages: sessionMessages, refetch: refetchSessionMessages, isLoading: messagesLoading } = useSessionMessages(effectiveSessionId, 'job_search');

    // Get all sessions to find current session context
    const { sessions } = useChatSessions('job_search');
    const currentSession = Array.isArray(sessions) ? sessions.find((session: any) => session.id.toString() === effectiveSessionId) : undefined;
    const sessionContext = currentSession?.context_data;

    // Custom job click handler
    const handleJobClick = (jobId: string) => {
        navigate(`/jobs/${jobId}`);
    };

    const handleSessionChange = (newSessionId: string) => {
        if (!newSessionId) {
            console.warn('handleSessionChange called with empty sessionId');
            return;
        }
        navigate(`/ai-jobs/${newSessionId}`, { replace: true });
    };

    const handleNewSession = () => {
        navigate('/ai-jobs', { replace: true });
        setActiveTab('chat'); // Switch to chat tab when starting new session
    };

    const customComponents = [
        StoredJobsButtonComponent
    ];

    const handleStateChange = (state: { hasStoredJobs?: boolean; matchedJobs?: unknown[]; showJobsList?: boolean; isComplete?: boolean; readyForJobSearch?: boolean }) => {
        // Auto-switch to jobs tab ONLY when user sends a message and jobs become ready
        // Don't auto-switch if user manually clicked the chat tab
        if (state.readyForJobSearch && activeTab === 'chat' && initialLoadComplete) {
            setActiveTab('jobs');

            // Refetch both jobs and messages when auto-switching
            Promise.all([
                refetchAiJobs(),
                refetchSessionMessages()
            ]).catch(console.error);
        }
    };

    const handleShowSuggestedJobs = async () => {
        setActiveTab('jobs');
        // Refetch both jobs and messages when button is clicked
        await Promise.all([
            refetchAiJobs(),
            refetchSessionMessages()
        ]);
    };

    useEffect(() => {
        if (!initialLoadComplete && (!effectiveSessionId || (!isLoadingJobs && aiJobs !== undefined))) {
            // Jobs data is ready (or no session exists), determine which tab to show
            if (aiJobs && aiJobs.length > 0) {
                setActiveTab('jobs');
            } else {
                setActiveTab('chat');
            }
            setInitialLoadComplete(true);
        }
    }, [initialLoadComplete, isLoadingJobs, aiJobs, effectiveSessionId]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
            }
        };
    }, []);

    const refreshJobs = async () => {
        await refetchAiJobs();
    };

    const retryFetchJobsWithDelay = async (attempt: number = 1) => {
        const maxRetries = 3;
        const delayMs = 2000; // 2 seconds between retries

        if (attempt > maxRetries) {
            setIsRetryingJobs(false);
            return;
        }

        setIsRetryingJobs(true);

        const result = await refetchAiJobs();

        if (result.data && result.data.length > 0) {
            // Jobs found! Stop retrying
            setIsRetryingJobs(false);
            retryCountRef.current = 0;
        } else {
            // No jobs yet, schedule next retry
            retryTimeoutRef.current = setTimeout(() => {
                retryFetchJobsWithDelay(attempt + 1);
            }, delayMs);
        }
    };

    // Show loader ONLY during initial load (before we've determined the tab)
    // Once initialLoadComplete is true, never show loader again (even during session switches)
    if (!initialLoadComplete) {
        return (
            <div>
                <PageTitle title="Jobsuche mit KI" />
                <MainContainer>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '50vh',
                        flexDirection: 'column',
                        gap: '16px'
                    }}>
                        <div style={{ fontSize: '16px', color: '#666' }}>
                            {isLoading ? 'Lade letzte AI Jobs-Sitzung...' : 'Lade Jobs Daten...'}
                        </div>
                    </div>
                </MainContainer>
            </div>
        );
    }

    // activeTab is guaranteed to be set by this point
    const effectiveActiveTab = activeTab || 'chat';

    const handleTabChange = async (key: string) => {
        setActiveTab(key);

        // Auto-refetch jobs and messages when switching to jobs tab
        if (key === 'jobs') {
            // Clear any existing retry timeout
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
                retryTimeoutRef.current = null;
            }

            try {
                const [jobsResult] = await Promise.all([
                    refetchAiJobs(),
                    refetchSessionMessages()
                ]);

                // If no jobs found on first fetch, start retry logic
                if (!jobsResult.data || jobsResult.data.length === 0) {
                    retryFetchJobsWithDelay(1);
                }
            } catch (error) {
                console.error('Error fetching jobs/messages:', error);
            }
        }
    };

    return (
        <div>
            <PageTitle title="Jobsuche mit KI" />
            <MainContainer>
                    {effectiveActiveTab === 'jobs' ? (
                        <div>
                            {/* Summary Card at the top */}
                            <AiJobsSummaryCard
                                sessionId={effectiveSessionId || null}
                                messages={sessionMessages || []}
                                jobsCount={aiJobs?.length || 0}
                                onViewChat={() => setActiveTab('chat')}
                                isLoadingMessages={messagesLoading}
                                sessionContext={sessionContext}
                                onNewChat={handleNewSession}
                            />

                            {/* Jobs List below */}
                            <JobsListComponent
                                state={{
                                    showJobsList: true,
                                    matchedJobs: aiJobs || [],
                                    isLoadingJobs: isRetryingJobs
                                }}
                                onRefreshJobs={refreshJobs}
                                sessionId={effectiveSessionId}
                                messages={sessionMessages || []}
                                messagesLoading={messagesLoading}
                                onViewChat={() => setActiveTab('chat')}
                                sessionContext={sessionContext}
                                onJobClick={handleJobClick}
                            />
                        </div>
                    ) : (
                        <div style={{ position: 'relative' }}>
                            <GenericChat
                                adapter={adapter}
                                customComponents={customComponents}
                                useTextArea={true}
                                placeholder="Erzählen Sie mir von Ihren Jobwünschen..."
                                loadingText="AI Jobs Agent schreibt..."
                                loginPrompt="Bitte melden Sie sich an, um Ihre Job-Suche zu starten."
                                showSidebar={true}
                                sidebarAlwaysVisible={true}
                                onSessionChange={handleSessionChange}
                                initialSessionId={effectiveSessionId}
                                onNewSession={handleNewSession}
                                onStateChange={handleStateChange}
                                onShowSuggestedJobs={handleShowSuggestedJobs}
                                conversationType="job_search"
                                hasActiveSubscription={true}
                            />
                        </div>
                    )}
            </MainContainer>
        </div>
    );
};
